import React from 'react';
import {
    GiftCardTransaction,
    fakeItemsArray,
    useI18n,
    useSpringBigStore,
    useTransactionDetails,
    useTransactions,
} from '@springbig/shared';
import Section from '~/shared/Section';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import {
    Box,
    Center,
    ChevronLeftIcon,
    ChevronRightIcon,
    HStack,
    Skeleton,
    VStack,
} from 'native-base';
import { useAuthStyles, useGeneralStyles } from '~/shared/styles';
import OurPressable from '~/shared/Pressable';
import { format } from 'date-fns';
import TransactionHeader from './TransactionHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { SpIcon } from '~/shared/Icon';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';

const Transactions = () => {

    const { params } = useAppNavigate();
    const navigate = useNavigate();
    const { state } = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const gStyle = useGeneralStyles();
    const { walletId } = useSpringBigStore();
    const { icon } = useAuthStyles();

    const { isLoading, giftCardTransactions, onNextPress, onPrevPress } =
        useTransactions(params, navigate, pageParam);

    return (
        <Section h='100%'>
            <TransactionHeader />
            <OurPressable
                onPress={() => navigate({ pathname: `/w/${walletId}/giftCard` })}
                ml={4}
            >
                <ChevronLeftIcon size='lg' mt={4} color={gStyle.button_background} />
            </OurPressable>

            <Box px={4}>
                {giftCardTransactions &&
                giftCardTransactions.transactions.length > 0 && (
                    <HStack justifyContent={'space-between'} pt={7}>
                        <Caption text='giftCard.transactions.details.titleDate' />
                        <Caption text='giftCard.transactions.details.titleLoadAmount' />
                    </HStack>
                )}

                { isLoading ?
                    fakeItemsArray.map((_, index) => (

                        <TransactionSkeleton key={index} />
                    ))
                    : giftCardTransactions &&
                        giftCardTransactions.transactions.length > 0 &&
                        giftCardTransactions.transactions.map((transaction, index) => (

                            <Box my={5} key={index}>
                                <Transaction transaction={transaction} />
                            </Box>
                        ))
                }
            </Box>

            {
                giftCardTransactions &&
                giftCardTransactions.transactions.length > 0 &&
                    pageParam ? (
                        <HStack px={4} justifyContent={'space-between'} pb={10}>
                            { parseInt(pageParam) > 1 && (
                                <OurPressable
                                    justifyContent={'center'}
                                    mr={2}
                                    onPress={() => onPrevPress(state)}
                                >
                                    <Caption
                                        text='giftCard.transactions.prevPage'
                                        color={gStyle.button_background}
                                    />
                                </OurPressable>
                            )}
                            <VStack>
                                <Caption
                                    text='giftCard.transactions.total'
                                    textValues={[giftCardTransactions.count.toString()]}
                                />
                                <Caption
                                    text='giftCard.transactions.pageOf'
                                    textValues={[
                                        pageParam,
                                        Math.ceil(giftCardTransactions.count / 10).toString(),
                                    ]}
                                />
                            </VStack>
                            { giftCardTransactions.count / 10 > parseInt(pageParam) && (
                                <OurPressable
                                    justifyContent={'center'}
                                    mr={2}
                                    onPress={() => onNextPress(state)}
                                >
                                    <Caption
                                        text='giftCard.transactions.nextPage'
                                        color={gStyle.button_background}
                                    />
                                </OurPressable>
                            )}
                        </HStack>
                    ) : (
                        <VStack
                            flex={1}
                            h={'100%'}
                            alignContent={'center'}
                            space={8}
                            justifyContent={'center'}
                        >
                            <Caption
                                textAlign={'center'}
                                text='giftCard.transactions.empty_state'
                            />
                            <Center>
                                <SpIcon icon={faReceipt} size={100} color={icon} />
                            </Center>
                        </VStack>
                    )}
        </Section>
    );
};

const TransactionSkeleton = () => {

    const gStyle = useGeneralStyles();

    return (
        <HStack w='100%'>
            <HStack
                space={8}
                rounded='md'
                justifyContent={'space-between'}
                my={5}
                flex={12}
                mr={3}
            >
                <Skeleton.Text lines={2} w={20} />
                <VStack>
                    <Skeleton.Text lines={1} w={20} justifyContent={'end'} />
                    <Skeleton.Text
                        lines={1}
                        w={10}
                        justifyContent={'end'}
                        mt={3}
                        alignSelf={'end'}
                    />
                </VStack>
            </HStack>
            <Box>
                <ChevronRightIcon
                    alignSelf={'center'}
                    size='lg'
                    color={gStyle.button_background}
                    flex={1}
                />
            </Box>
        </HStack>
    );
};

const Transaction = ({ transaction }: { transaction: GiftCardTransaction }) => {

    const { t } = useI18n();
    const gStyle = useGeneralStyles();
    const { navigate, params } = useAppNavigate();
    const { state: { cardNumber } } = useLocation();
    const { walletId, merchant } = useSpringBigStore();
    const { isLoading, transactionRecords } = useTransactionDetails(
        params,
        transaction,
    );

    const dollarPerPoint = parseFloat(merchant?.dollars_per_point || '0.0');

    const onTransactionSelected = () => {

        if (walletId && params.gift_card_id && transaction.id) {
            navigate(
                `w/${walletId}/giftCard/${params.gift_card_id}/transaction/${transaction.id}`,
                {
                    state: { transaction, cardNumber },
                },
            );
        }
    };

    const returnHead = () => {

        if (transactionRecords?.type === t('giftCard.transactions.details.nmi')) {
            return <Caption text='giftCard.transactions.card_load' />;
        }

        return <Caption>{ transactionRecords?.type }</Caption>;
    };

    const returnSubHead = () => {

        if (transactionRecords?.type === t('giftCard.transactions.details.nmi')) {
            return (
                <Caption
                    textAlign={'right'}
                    text='giftCard.transactions.processing_fee'
                    textValues={[transaction.process_fee.toString()]}
                />
            );
        }
        else if (transactionRecords.type === t('giftCard.transactions.details.points_for_cash')) {
            return (
                <Caption
                    textAlign={'right'}
                    text='giftCard.transactions.transaction_points'
                    textValues={[(transaction.load_amount_cents / 100 * dollarPerPoint).toString()]}
                />
            );
        }
    };

    if (isLoading) {
        return <Skeleton />;
    }

    return (
        <HStack justifyContent={'space-between'}>
            <HStack justifyContent={'space-between'} flex={1} mr={3}>
                <VStack>
                    <Caption fontSize={18}>
                        { format(new Date(transaction.created_at), 'MM-dd-yyyy') }
                    </Caption>
                    { returnHead() }
                </VStack>
                <VStack>
                    <Caption fontSize={18} textAlign={'left'}>
                        ${transaction.load_amount_cents / 100}
                    </Caption>
                    { returnSubHead() }
                </VStack>
            </HStack>
            <OurPressable onPress={onTransactionSelected} justifyContent={'center'}>
                <ChevronRightIcon
                    alignSelf={'center'}
                    size='lg'
                    color={gStyle.button_background}
                />
            </OurPressable>
        </HStack>
    );
};

export default Transactions;
